import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Search, Trash2, ShoppingCart, FileText, Plus, Minus, PlusCircle, Package, Printer, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import QuotationModal from "@/components/QuotationModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Quotations = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [cart, setCart] = useState([]);
  const [quickAccessProducts, setQuickAccessProducts] = useState([]);
  const [priceLevel, setPriceLevel] = useState(1);
  const [quotationModalOpen, setQuotationModalOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [queryFormOpen, setQueryFormOpen] = useState(false);
  const queryFormRef = useRef(null);
  
  // Once-off product dialog state
  const [onceOffDialogOpen, setOnceOffDialogOpen] = useState(false);
  const [onceOffName, setOnceOffName] = useState("");
  const [onceOffPrice, setOnceOffPrice] = useState("");
  const [onceOffQuantity, setOnceOffQuantity] = useState("1");
  const [addToStockMaster, setAddToStockMaster] = useState(false);

  // Print Customer Query Form
  const handlePrintQueryForm = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error("Pop-up blocked. Please allow pop-ups for printing.");
      return;
    }
    
    const rows = Array(15).fill(null).map((_, i) => `
      <tr>
        <td style="height: 28px; border: 1px solid #000;"></td>
        <td style="border: 1px solid #000;"></td>
        <td style="border: 1px solid #000;"></td>
        <td style="border: 1px solid #000;"></td>
        <td style="border: 1px solid #000;"></td>
      </tr>
    `).join('');
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Customer Query Form</title>
          <style>
            @page { size: A4; margin: 8mm; }
            @media print {
              html, body { height: 100%; }
            }
            body { 
              font-family: Arial, sans-serif; 
              padding: 5mm;
              color: #000;
              margin: 0;
            }
            .header {
              text-align: center;
              margin-bottom: 10px;
            }
            .header h1 {
              font-size: 20px;
              margin: 0;
              text-transform: uppercase;
            }
            .header h2 {
              font-size: 16px;
              margin: 3px 0;
              font-weight: normal;
            }
            .date-line {
              text-align: right;
              margin-bottom: 10px;
              font-size: 12px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
            }
            th {
              background-color: #333;
              color: white;
              padding: 8px;
              text-align: left;
              font-size: 12px;
              border: 1px solid #000;
            }
            td {
              padding: 5px;
            }
            .footer {
              margin-top: 10px;
              font-size: 10px;
              text-align: center;
              color: #666;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>BOSVELD BOU & HARDEWARE</h1>
            <h2>Customer Query Form</h2>
          </div>
          
          <div class="date-line">
            Date: _____________________
          </div>
          
          <table>
            <thead>
              <tr>
                <th style="width: 25%;">Customer Name</th>
                <th style="width: 35%;">Product Description</th>
                <th style="width: 10%;">Qty</th>
                <th style="width: 15%;">To Order</th>
                <th style="width: 15%;">Have in Stock</th>
              </tr>
            </thead>
            <tbody>
              ${rows}
            </tbody>
          </table>
          
          <div class="footer">
            BOSVELD BOU & HARDEWARE - Customer Query Form
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
  };

  useEffect(() => {
    fetchQuickAccessProducts();
  }, []);

  const fetchQuickAccessProducts = async () => {
    try {
      const response = await axios.get(`${API}/products/quick-access`);
      setQuickAccessProducts(response.data);
    } catch (error) {
      console.error("Error fetching quick access products:", error);
    }
  };

  const handleSearch = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    try {
      const response = await axios.get(`${API}/products/search`, {
        params: { q: query }
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error("Error searching products:", error);
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        handleSearch(searchQuery);
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const getProductPrice = (product) => {
    let price;
    switch (priceLevel) {
      case 2:
        price = product.retail_price_level2 || product.selling_price_2 || product.selling_price_incl_vat || product.retail_price_level1 || product.selling_price;
        break;
      case 3:
        price = product.retail_price_level3 || product.selling_price_3 || product.selling_price_incl_vat || product.retail_price_level1 || product.selling_price;
        break;
      default:
        price = product.selling_price_incl_vat || product.retail_price_level1 || product.selling_price || product.retail_price_level2;
    }
    return price || 0;
  };

  const addToCart = (product) => {
    const existingItem = cart.find((item) => item.product_id === product.id);
    const price = getProductPrice(product);
    
    if (existingItem) {
      setCart(
        cart.map((item) =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + 1, total: (item.quantity + 1) * item.unit_price }
            : item
        )
      );
    } else {
      setCart([
        ...cart,
        {
          product_id: product.id,
          name: product.name,
          quantity: 1,
          unit_price: price,
          // Shelf snapshot so QuotationModal can restore / re-strip cleanly
          // when a VAT-exempt customer is picked in the modal.
          _shelf_unit_price: price,
          // VAT metadata needed by QuotationModal to honour the customer's
          // VAT-exemption status. Matches the fields Sales.jsx stamps.
          vat_category: product.vat_category || "Standard 15%",
          vat_exempt_price_incl_vat: Number(
            product.vat_exempt_price_incl_vat ?? product.promotion_price_incl_vat ?? 0
          ) || 0,
          discount_percent: 0,
          total: price,
          no_discount: product.no_discount || false,
          is_once_off: false
        },
      ]);
    }
    
    // Clear search after adding
    setSearchQuery("");
    setSearchResults([]);
    toast.success(`${product.name} added to quote`);
  };

  const updateQuantity = (productId, delta) => {
    setCart(
      cart.map((item) => {
        if (item.product_id === productId) {
          const newQuantity = Math.max(0.001, item.quantity + delta);
          const discountAmount = (item.unit_price * newQuantity * (item.discount_percent || 0)) / 100;
          return { ...item, quantity: newQuantity, qty_input: undefined, total: (newQuantity * item.unit_price) - discountAmount };
        }
        return item;
      })
    );
  };

  // Keep what's typed ("0", "0.", "") so decimals like 0.73 kg can be entered.
  const setQuantity = (productId, newQuantity) => {
    const raw = String(newQuantity).replace(",", ".");
    const qty = parseFloat(raw);
    setCart(
      cart.map((item) => {
        if (item.product_id === productId) {
          const validQty = qty > 0 ? qty : item.quantity;
          const discountAmount = (item.unit_price * validQty * (item.discount_percent || 0)) / 100;
          return { ...item, quantity: validQty, qty_input: raw, total: (validQty * item.unit_price) - discountAmount };
        }
        return item;
      })
    );
  };

  const finishQuantity = (productId) => {
    setCart((prev) => prev.map((item) => (item.product_id === productId ? { ...item, qty_input: undefined } : item)));
  };

  const updatePrice = (productId, newPrice) => {
    const price = parseFloat(newPrice) || 0;
    setCart(
      cart.map((item) => {
        if (item.product_id === productId) {
          const discountAmount = (price * item.quantity * (item.discount_percent || 0)) / 100;
          return { ...item, unit_price: price, total: (item.quantity * price) - discountAmount };
        }
        return item;
      })
    );
  };

  const updateDiscount = (productId, newDiscount) => {
    const discount = parseFloat(newDiscount) || 0;
    const validDiscount = Math.min(100, Math.max(0, discount));
    setCart(
      cart.map((item) => {
        if (item.product_id === productId) {
          const discountAmount = (item.unit_price * item.quantity * validDiscount) / 100;
          return { ...item, discount_percent: validDiscount, total: (item.quantity * item.unit_price) - discountAmount };
        }
        return item;
      })
    );
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter((item) => item.product_id !== productId));
  };

  const clearCart = () => {
    setCart([]);
  };

  // Add once-off product
  const handleAddOnceOff = async () => {
    if (!onceOffName.trim()) {
      toast.error("Please enter a product name");
      return;
    }
    if (!onceOffPrice || parseFloat(onceOffPrice) <= 0) {
      toast.error("Please enter a valid price");
      return;
    }

    const price = parseFloat(onceOffPrice);
    const quantity = parseFloat(onceOffQuantity) || 1;
    const productId = `once-off-${Date.now()}`;

    // If user wants to add to stock master
    if (addToStockMaster) {
      try {
        const newProduct = {
          name: onceOffName.trim(),
          selling_price: price,
          selling_price_2: price,
          selling_price_3: price,
          cost_price: 0,
          current_stock: 0,
          minimum_level: 0,
          reorder_level: 0,
          maximum_level: 0,
          barcode: "",
          category: "General",
          department: "General",
          quick_access: false
        };
        
        const response = await axios.post(`${API}/products`, newProduct);
        toast.success(`"${onceOffName}" added to Stock Master`);
        
        // Add to cart with the new product ID
        setCart([
          ...cart,
          {
            product_id: response.data.id,
            name: onceOffName.trim(),
            quantity: quantity,
            unit_price: price,
            discount_percent: 0,
            total: quantity * price,
            no_discount: false,
            is_once_off: false
          }
        ]);
      } catch (error) {
        console.error("Error adding to stock master:", error);
        toast.error("Failed to add to Stock Master");
        return;
      }
    } else {
      // Just add to cart as once-off
      setCart([
        ...cart,
        {
          product_id: productId,
          name: onceOffName.trim(),
          quantity: quantity,
          unit_price: price,
          discount_percent: 0,
          total: quantity * price,
          no_discount: false,
          is_once_off: true
        }
      ]);
      toast.success(`"${onceOffName}" added to quote`);
    }

    // Reset form and close dialog
    setOnceOffName("");
    setOnceOffPrice("");
    setOnceOffQuantity("1");
    setAddToStockMaster(false);
    setOnceOffDialogOpen(false);
  };

  const calculateTotals = () => {
    const subtotalInclVat = cart.reduce((sum, item) => sum + item.total, 0);
    const subtotal = subtotalInclVat / 1.15;
    const tax = subtotalInclVat - subtotal;
    return { subtotal, tax, total: subtotalInclVat };
  };

  const { subtotal, tax, total } = calculateTotals();

  const handleCreateQuotation = () => {
    if (cart.length === 0) {
      toast.error("Please add items to create a quotation");
      return;
    }
    setQuotationModalOpen(true);
  };

  const handleQuotationComplete = () => {
    setQuotationModalOpen(false);
    setCart([]);
  };

  return (
    <div className="flex h-[calc(100vh-80px)]" data-testid="quotations-page">
      {/* Left Panel - Product Search and Quick Access */}
      <div className="flex-1 p-4 overflow-y-auto">
        {/* Search Bar */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
          <Input
            type="text"
            placeholder="Search products by name, barcode, or PLU..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 text-lg"
            data-testid="product-search-input"
          />
          {searching && (
            <div className="absolute right-3 top-3 text-sm text-gray-500">Searching...</div>
          )}
        </div>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <Card className="mb-4">
            <CardContent className="p-2">
              <div className="text-sm text-gray-500 mb-2">Search Results ({searchResults.length})</div>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {searchResults.map((product) => (
                  <div
                    key={product.id}
                    onClick={() => addToCart(product)}
                    className="flex justify-between items-center p-3 hover:bg-blue-50 cursor-pointer rounded border"
                  >
                    <div>
                      <div className="font-semibold">{product.name}</div>
                      <div className="text-sm text-gray-500">
                        {product.barcode} • Stock: {product.current_stock}
                      </div>
                    </div>
                    <div className="text-lg font-bold text-blue-600">
                      R {getProductPrice(product).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Price Level & Once-off Product */}
        <div className="flex gap-2 mb-4 items-center justify-between flex-wrap">
          <div className="flex gap-2 items-center">
            <span className="text-sm text-gray-600">Price Level:</span>
            {[1, 2, 3].map((level) => (
              <Button
                key={level}
                variant={priceLevel === level ? "default" : "outline"}
                size="sm"
                onClick={() => setPriceLevel(level)}
                className={priceLevel === level ? "bg-blue-600" : ""}
              >
                Level {level}
              </Button>
            ))}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handlePrintQueryForm}
              variant="outline"
              className="border-purple-500 text-purple-700 hover:bg-purple-50"
            >
              <ClipboardList className="h-4 w-4 mr-2" />
              Print Query Form
            </Button>
            <Button
              onClick={() => setOnceOffDialogOpen(true)}
              variant="outline"
              className="border-green-500 text-green-700 hover:bg-green-50"
            >
              <PlusCircle className="h-4 w-4 mr-2" />
              Add Once-Off Item
            </Button>
          </div>
        </div>

        {/* Quick Access Products */}
        <div>
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Quick Access Products
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {quickAccessProducts.map((product) => (
              <Card
                key={product.id}
                className="cursor-pointer hover:shadow-md transition-shadow hover:border-blue-400"
                onClick={() => addToCart(product)}
              >
                <CardContent className="p-3">
                  <div className="font-semibold text-sm truncate">{product.name}</div>
                  <div className="text-lg font-bold text-blue-600 mt-1">
                    R {getProductPrice(product).toFixed(2)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Stock: {product.current_stock}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Cart */}
      <div className="w-[420px] bg-white border-l flex flex-col">
        {/* Cart Header */}
        <div className="p-4 border-b bg-blue-50">
          <h2 className="text-xl font-bold flex items-center gap-2 text-blue-800">
            <FileText className="h-6 w-6" />
            Quote Items ({cart.length})
          </h2>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4">
          {cart.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Add items to create a quotation</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cart.map((item) => (
                <Card key={item.product_id} className={`p-3 ${item.is_once_off ? 'border-green-300 bg-green-50' : ''}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-semibold text-sm flex items-center gap-2">
                        {item.name}
                        {item.is_once_off && (
                          <span className="text-xs bg-green-200 text-green-800 px-1.5 py-0.5 rounded">
                            Once-off
                          </span>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFromCart(item.product_id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 -mr-2 -mt-1"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  {/* Editable Price */}
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-sm text-gray-500">Price:</span>
                    <div className="relative flex-1">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500">R</span>
                      <Input
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => updatePrice(item.product_id, e.target.value)}
                        className="pl-6 h-8 text-sm"
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </div>

                  {/* Discount Percentage */}
                  {!item.no_discount && (
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-sm text-gray-500">Discount:</span>
                      <div className="relative flex-1">
                        <Input
                          type="number"
                          value={item.discount_percent || 0}
                          onChange={(e) => updateDiscount(item.product_id, e.target.value)}
                          className="pr-6 h-8 text-sm"
                          step="1"
                          min="0"
                          max="100"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">%</span>
                      </div>
                    </div>
                  )}

                  <div className="flex justify-between items-center mt-2">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateQuantity(item.product_id, -1)}
                        className="h-8 w-8 p-0"
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={item.qty_input ?? item.quantity}
                        onChange={(e) => setQuantity(item.product_id, e.target.value)}
                        onBlur={() => finishQuantity(item.product_id)}
                        onFocus={(e) => e.target.select()}
                        className="w-20 h-8 text-center font-semibold"
                        data-testid={`quote-qty-input-${item.product_id}`}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => updateQuantity(item.product_id, 1)}
                        className="h-8 w-8 p-0"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="font-bold text-blue-600">
                      R {item.total.toFixed(2)}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Totals & Create Quote */}
        <div className="border-t p-4 space-y-3">
          <div className="space-y-2">
            <div className="flex justify-between text-lg">
              <span>Subtotal:</span>
              <span className="font-semibold">R {subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg">
              <span>VAT (15%):</span>
              <span className="font-semibold">R {tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-2xl font-bold border-t pt-2">
              <span>Total:</span>
              <span className="text-blue-600" data-testid="quote-total">R {total.toFixed(2)}</span>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={clearCart}
              className="flex-1"
              disabled={cart.length === 0}
              data-testid="clear-quote-button"
            >
              Clear
            </Button>
            <Button
              onClick={handleCreateQuotation}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-lg h-12"
              disabled={cart.length === 0}
              data-testid="create-quote-button"
            >
              <FileText className="mr-2" />
              Create Quote
            </Button>
          </div>
        </div>
      </div>

      {/* Once-Off Product Dialog */}
      <Dialog open={onceOffDialogOpen} onOpenChange={setOnceOffDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-green-600" />
              Add Once-Off Item
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="mb-2 block">Product/Service Name *</Label>
              <Input
                placeholder="Enter product or service name..."
                value={onceOffName}
                onChange={(e) => setOnceOffName(e.target.value)}
                autoFocus
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">Price (Incl VAT) *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R</span>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={onceOffPrice}
                    onChange={(e) => setOnceOffPrice(e.target.value)}
                    className="pl-8"
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>
              <div>
                <Label className="mb-2 block">Quantity</Label>
                <Input
                  type="number"
                  value={onceOffQuantity}
                  onChange={(e) => setOnceOffQuantity(e.target.value)}
                  min="1"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
              <Checkbox
                id="addToStock"
                checked={addToStockMaster}
                onCheckedChange={setAddToStockMaster}
              />
              <Label htmlFor="addToStock" className="text-sm cursor-pointer">
                Also add this item to Stock Master
              </Label>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setOnceOffDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddOnceOff}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add to Quote
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quotation Modal */}
      {quotationModalOpen && (
        <QuotationModal
          open={quotationModalOpen}
          onClose={() => setQuotationModalOpen(false)}
          cart={cart}
          calculateTotals={calculateTotals}
          priceLevel={priceLevel}
          onComplete={handleQuotationComplete}
        />
      )}
    </div>
  );
};

export default Quotations;
